import { useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Badge, Button, Card, CardHeader, ConfirmDialog, ErrorState, InfoRow, PageHeader, PageLoader, useToast } from '../../../components/ui';
import { useCrumb } from '../../../components/layout/Breadcrumbs/Breadcrumbs';
import { KeyValues, TwoColumn } from '../../../components/common/Parts/Parts';
import { useAction } from '../../../hooks/useAction';
import { useFetch } from '../../../hooks/useFetch';
import { money } from '../../../utils/currency';
import { MovementsCard } from '../../stock/components/MovementsCard/MovementsCard';
import { itemService } from '../services/materialService';

/** Material page: details, rates and stock history. */
export default function MaterialDetails() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { busy, run } = useAction(toast);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { data: i, error, reload } = useFetch(() => itemService.get(id), [id]);
  useCrumb(`/materials/${id}`, i?.name);

  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!i) return <PageLoader />;

  // Point-wise work items live in Work rates.
  if (i.type === 'service') return <Navigate to={`/work-rates/${id}/edit`} replace />;
  const material = i.type === 'material';
  const low = material && (i.currentStock ?? 0) <= (i.minStockLevel ?? 0);

  const remove = async () => {
    if (await run('delete', () => itemService.remove(id), `${i.name} deleted`)) navigate('/materials', { replace: true });
    setConfirmDelete(false);
  };

  return (
    <>
      <PageHeader
        title={i.name}
        badge={i.isActive ? <Badge tone="green" dot>Active</Badge> : <Badge tone="grey" dot>Inactive</Badge>}
        subtitle={[i.itemCode, i.category, i.brand].filter(Boolean).join(' · ')}
        actions={
          <>
            <Button variant="dangerText" onClick={() => setConfirmDelete(true)}>Delete</Button>
            <Button variant="outline" leftIcon="edit" onClick={() => navigate(`/materials/${id}/edit`)}>Edit</Button>
            {material && <Button leftIcon="layers" onClick={() => navigate(`/stock/adjust?itemId=${id}`)}>Adjust stock</Button>}
          </>
        }
      />
      <TwoColumn
        main={
          <>
            <Card className="p-5">
              <dl className="grid gap-5 sm:grid-cols-3">
                <InfoRow label="Brand">{i.brand}</InfoRow>
                <InfoRow label="Specification">{i.specification}</InfoRow>
                <InfoRow label="Unit">{i.unit}</InfoRow>
                <InfoRow label="HSN">{i.sacHsn}</InfoRow>
                <InfoRow label="GST">{`${i.gstRate ?? 0}%`}</InfoRow>
                <InfoRow label="Category">{i.category}</InfoRow>
              </dl>
            </Card>
            {material && <MovementsCard itemId={id} />}
          </>
        }
        aside={
          <>
            <Card>
              <CardHeader title="Rates" />
              <div className="p-5 pt-4">
                <KeyValues
                  rows={[
                    ['Selling rate', `${money(i.sellingRate)} / ${i.unit}`, { strong: true }],
                    ['Last purchase rate', money(i.purchaseRate)],
                    material && ['Average cost', money(i.avgPurchaseRate)],
                  ]}
                />
              </div>
            </Card>
            {material && (
              <Card>
                <CardHeader title="Stock" action={low ? <Badge tone="red" dot>Low</Badge> : null} />
                <div className="p-5 pt-4">
                  <KeyValues
                    rows={[
                      ['In stock', `${i.currentStock ?? 0} ${i.unit}`, { strong: true, tone: low ? 'text-red-600' : undefined }],
                      ['Minimum level', `${i.minStockLevel ?? 0} ${i.unit}`],
                      ['Stock value', money(Math.round((i.currentStock ?? 0) * (i.avgPurchaseRate ?? 0)))],
                    ]}
                  />
                  {low && (
                    <Button variant="outline" size="sm" fullWidth leftIcon="cart" className="mt-4" onClick={() => navigate('/purchases/new')}>
                      Buy more
                    </Button>
                  )}
                </div>
              </Card>
            )}
          </>
        }
      />
      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={remove}
        loading={busy === 'delete'}
        danger
        title="Delete this material?"
        message="It will be hidden from lists and pickers. Old quotations and bills keep their copy."
        confirmLabel="Delete material"
      />
    </>
  );
}
